import { createConsumer, type Consumer, type Subscription } from '@rails/actioncable';
import { log } from '@/app/shared/utils';

type AccessTokenGetter = () => string | null | undefined | Promise<string | null | undefined>;

type Identifier = Record<string, unknown>;

type Handlers<T> = {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onData?: (data: T) => void;
};

type StoredSub = {
  identifier: Identifier;
  handlers: Handlers<any>;
  subscription: Subscription | null;
};

export interface WsSubscription {
  perform(action: string, data?: Record<string, unknown>): void;
  unsubscribe(): void;
}

export class WsTransport {
  private accessTokenGetter?: AccessTokenGetter;
  private consumer: Consumer | null = null;
  private tokenInUse: string | null = null;
  private subs = new Set<StoredSub>();

  constructor(private readonly baseWsUrl: string) {}

  private async ensureConsumer(): Consumer {
    if (!this.accessTokenGetter) {
      throw new Error('No accessTokenGetter');
    }

    const token = await this.accessTokenGetter();

    if (!token) {
      throw new Error('No JWT token for WS');
    }

    if (!this.consumer || this.tokenInUse !== token) {
      this.reconnect(token);
    }

    return this.consumer!;
  }

  private reconnect(token: string) {
    this.consumer?.disconnect();
    this.consumer = null;

    this.tokenInUse = token;
    this.consumer = createConsumer(`${this.baseWsUrl}/cable?token=${encodeURIComponent(token)}`);

    log('ws consumer created');

    for (const s of this.subs) {
      s.subscription?.unsubscribe();
      s.subscription = this.consumer.subscriptions.create(s.identifier, {
        connected: s.handlers.onConnected,
        disconnected: s.handlers.onDisconnected,
        received: s.handlers.onData,
      });
    }
  }

  async subscribe<T = unknown>(identifier: Identifier, handlers: Handlers<T>): Promise<WsSubscription> {
    const consumer = await this.ensureConsumer();

    const stored: StoredSub = { identifier, handlers, subscription: null };

    stored.subscription = consumer.subscriptions.create(identifier, {
      connected: handlers.onConnected,
      disconnected: handlers.onDisconnected,
      received: handlers.onData,
    });

    this.subs.add(stored);

    return {
      perform: (action: string, data: any) => {
        stored.subscription?.perform(action, data);
      },
      unsubscribe: () => {
        stored.subscription?.unsubscribe();
        this.subs.delete(stored);
      },
    };
  }

  // TODO: need to listen for token refreshed event in auth (not implemented) and trigger it
  async onTokenRefreshed() {
    if (!this.accessTokenGetter) return;
    const token = await this.accessTokenGetter();
    if (!token) return;
    if (!this.consumer) return;
    if (this.tokenInUse === token) return;
    this.reconnect(token);
  }

  disconnect() {
    for (const s of this.subs) s.subscription?.unsubscribe();
    this.subs.clear();
    this.consumer?.disconnect();
    this.consumer = null;
    this.tokenInUse = null;
  }

  setAccessTokenGetter(getter: AccessTokenGetter) {
    this.accessTokenGetter = getter;
  }
}
